/**
 * save/deserialize.ts — rebuild a live GameState from a SaveBlob.
 *
 * Inverse of serialize.ts. The static catalogues (shipTemplates,
 * weapons, techs) come from the freshly-loaded LoadedGameData rather
 * than from the blob — see serialize.ts header for the rationale.
 *
 * Validation strategy: cheap structural checks (formatVersion,
 * required top-level fields). We do *not* deep-validate every nested
 * record — saves come from our own serialiser, so a well-formed
 * blob with the matching formatVersion is trustworthy. If we ever
 * accept saves from untrusted sources (export / import), tighten
 * this.
 */

import type { LoadedGameData } from '../data/loader';
import { deriveStarClass } from '../state/star-class';
import type {
  Empire,
  EnvironmentEntity,
  FogOfWarSnapshot,
  GameState,
  BeamFire,
  Projectile,
  Planet,
  PlanetFoWState,
  ShipInstance,
  StarSystem,
  Structure,
  StructureFoWState,
  VisibilityRecord,
  WarpGateLink,
} from '../state/types/index';
import {
  SAVE_BLOB_VERSION,
  type SaveBlob,
  type SerializedEmpire,
  type SerializedFogOfWarSnapshots,
  type SerializedTeamVision,
} from './serialize';

/** Thrown when a SaveBlob can't be loaded (version mismatch, malformed). */
export class SaveLoadError extends Error {
  constructor(
    message: string,
    /** Hint for the UI — concise enough to surface in a toast. */
    readonly userMessage: string,
  ) {
    super(message);
    this.name = 'SaveLoadError';
  }
}

function deserializeEmpire(s: SerializedEmpire): Empire {
  return {
    id: s.id,
    name: s.name,
    raceId: s.raceId,
    isPlayer: s.isPlayer,
    resources: { ...s.resources },
    planetIds: [...s.planetIds],
    researchedTechs: new Set(s.researchedTechs),
    activeResearch: s.activeResearch ? { ...s.activeResearch } : null,
    shipIds: [...s.shipIds],
    color: s.color,
    aiDifficulty: s.aiDifficulty,
    // v4M8.1 — Empire field rename `aiBehaviour` → `aiArchetype`.
    // SerializedEmpire is `Omit<Empire, …>`, so the new field name flows
    // through automatically. Pre-v28 blobs reject at the version gate
    // (`validateBlobShape` in this file).
    aiArchetype: s.aiArchetype,
    // v4M3.1 — pre-v14 saves lack `teamId`; back-fill to `null` (the
    // "solo / legacy" default). v14+ saves carry the value verbatim.
    teamId: s.teamId ?? null,
    scoutedSystemIds: new Set(s.scoutedSystemIds),
    // v4M11.1 — in-system fog discovered sets per `IN_SYSTEM_FOG.md
    // §3`. Defensive coercion: pre-v30 blobs may have these fields
    // missing entirely (in which case `s.discoveredPlanetIds` is
    // undefined and `new Set(undefined)` returns an empty Set). The
    // lenient back-compat reconstruction (from scoutedSystemIds ×
    // planets/env per scouted system) runs at the post-deserialize
    // backfill pass in `deserialize()` so it has access to the full
    // state.systems + state.planets + state.environment maps.
    discoveredPlanetIds: new Set(s.discoveredPlanetIds ?? []),
    discoveredEnvironmentIds: new Set(s.discoveredEnvironmentIds ?? []),
    // MFix42.8a — research-progress retention + queue.
    // `parkedResearchProgress` is a `[name, rp][]` tuple array in the
    // serialised shape; pre-v32 blobs have the field missing entirely,
    // so `?? []` produces the empty-Map seed. With no parked entries
    // the MFix42.8b switch-helper behaves identically to the pre-
    // MFix42.8 reset-to-0 semantics — Alpha 0.6 saves carry over
    // without any behavioural surprise. `researchQueue` flows through
    // as `string[]` (not Omit'd in SerializedEmpire); the `?? []`
    // default similarly covers pre-v32 blobs.
    parkedResearchProgress: new Map(s.parkedResearchProgress ?? []),
    researchQueue: [...(s.researchQueue ?? [])],
    diplomaticFocus: s.diplomaticFocus,
  };
}

function deserializeTeamVision(
  serialized: SerializedTeamVision,
): GameState['teamVision'] {
  const out: GameState['teamVision'] = new Map();
  for (const [teamId, innerArr] of serialized) {
    const inner = new Map<number, VisibilityRecord>();
    for (const [shipId, rec] of innerArr) inner.set(shipId, { ...rec });
    out.set(teamId, inner);
  }
  return out;
}

/**
 * v4M10.1 — rebuild `state.fogOfWarSnapshots` from the flattened
 * serialised shape. Defensive: pre-v30 blobs that lack the field
 * deserialise with an empty Map (lenient back-compat per
 * `docs/FOW.md §3.3`). The first `advanceTick` after load re-populates
 * snapshots for every scouted system, so the empty seed is
 * non-load-bearing for sim correctness.
 */
function deserializeFogOfWarSnapshots(
  serialized: SerializedFogOfWarSnapshots | undefined,
): GameState['fogOfWarSnapshots'] {
  const out: GameState['fogOfWarSnapshots'] = new Map();
  if (!serialized) return out;
  for (const [empireId, perSystemArr] of serialized) {
    const perSystem = new Map<number, FogOfWarSnapshot>();
    for (const [sysId, snap] of perSystemArr) {
      const planets = new Map<number, PlanetFoWState>();
      for (const [pid, planetState] of snap.planets) {
        planets.set(pid, {
          ...planetState,
          tradingWith: [...planetState.tradingWith],
          influence: new Map(planetState.influence),
          structureIds: [...planetState.structureIds],
        });
      }
      const structures = new Map<number, StructureFoWState>();
      for (const [sid, structureState] of snap.structures) {
        structures.set(sid, { ...structureState });
      }
      perSystem.set(sysId, {
        lastSeenTick: snap.lastSeenTick,
        planets,
        structures,
      });
    }
    out.set(empireId, perSystem);
  }
  return out;
}

/** Top-level structural sanity check. Throws SaveLoadError on mismatch. */
function validateBlobShape(blob: unknown): asserts blob is SaveBlob {
  if (typeof blob !== 'object' || blob === null) {
    throw new SaveLoadError('Blob is not an object', 'Save file is corrupted.');
  }
  const obj = blob as Record<string, unknown>;
  if (typeof obj.header !== 'object' || obj.header === null) {
    throw new SaveLoadError(
      'Missing header',
      'Save file is corrupted — header missing.',
    );
  }
  if (typeof obj.body !== 'object' || obj.body === null) {
    throw new SaveLoadError(
      'Missing body',
      'Save file is corrupted — body missing.',
    );
  }
  const header = obj.header as Record<string, unknown>;
  if (typeof header.formatVersion !== 'number') {
    throw new SaveLoadError(
      'header.formatVersion missing or wrong type',
      'Save file is corrupted — version missing.',
    );
  }
  if (header.formatVersion !== SAVE_BLOB_VERSION) {
    throw new SaveLoadError(
      `formatVersion ${header.formatVersion} != current ${SAVE_BLOB_VERSION}`,
      `Save was created with format version ${header.formatVersion}; this build expects ${SAVE_BLOB_VERSION}. Saves can't be migrated automatically.`,
    );
  }
}

/**
 * Restore a GameState from a SaveBlob. The static catalogues
 * (shipTemplates, weapons, techs) are pulled from `data` — pass the
 * same LoadedGameData your fresh-game path would use.
 *
 * Throws {@link SaveLoadError} if the blob is malformed or its
 * formatVersion doesn't match this build.
 */
export function deserializeGameState(
  blob: unknown,
  data: LoadedGameData,
): GameState {
  validateBlobShape(blob);
  const body = blob.body;

  const empires = new Map<number, Empire>();
  for (const [id, s] of body.empires) empires.set(id, deserializeEmpire(s));

  const planets = new Map<number, Planet>();
  for (const [id, p] of body.planets) {
    // Defensive shallow copy so post-load mutations to the live state
    // don't accidentally write back into the blob. `terraforming` is
    // nullable; clone only when present.
    planets.set(id, {
      ...p,
      structureIds: [...p.structureIds],
      shipBuildQueue: p.shipBuildQueue.map((q) => ({ ...q })),
      terraforming: p.terraforming ? { ...p.terraforming } : null,
      // v3M21.1 — rebuild diplomacy fields. influence is a Map at
      // runtime, serialised as Array<[empireId, points]>; the others
      // are JSON-clean and ride through via spread above (this assignment
      // overrides the spread for `influence` only).
      influence: new Map(p.influence),
      tradingWith: [...p.tradingWith],
    });
  }

  const systems = new Map<number, StarSystem>();
  for (const [id, s] of body.systems) {
    systems.set(id, {
      ...s,
      planetIds: [...s.planetIds],
      adjacentSystemIds: [...s.adjacentSystemIds],
      // v4M6.1 — `starClass` is derived not persisted (per docs/MAP.md
      // §2.3 "Save format impact"). Recompute deterministically from
      // the system id; same value every load, no save format change.
      // Defensive override: even if a future build accidentally
      // persists the field, the derivation always wins on deserialize
      // so the data stays consistent.
      starClass: deriveStarClass(id),
    });
  }

  // v3M18.1 — restore Warp Gate connections. Tolerate older blobs that
  // pre-date the field by defaulting to an empty Map (the format version
  // bump rejects pre-v7 saves up front, so this branch is defensive only).
  const warpGateLinks = new Map<number, WarpGateLink>();
  if (body.warpGateLinks) {
    for (const [id, l] of body.warpGateLinks) warpGateLinks.set(id, { ...l });
  }

  const ships = new Map<number, ShipInstance>();
  for (const [id, s] of body.ships) {
    ships.set(id, {
      ...s,
      moveOrder: s.moveOrder ? { ...s.moveOrder } : null,
      constructionOrder: s.constructionOrder ? { ...s.constructionOrder } : null,
      bombardOrder: s.bombardOrder ? { ...s.bombardOrder } : null,
      // Restore Infinity from the -1 sentinel set by serialize.ts. Any
      // legitimate non-negative ammo passes through unchanged.
      weaponSlots: s.weaponSlots.map((w) => ({
        cooldownTicks: w.cooldownTicks,
        ammoRemaining: w.ammoRemaining === -1 ? Infinity : w.ammoRemaining,
      })),
    });
  }

  // v3M28.1 — persistent missile entities. Plain shallow clone per
  // entry; Missile carries only number / string / boolean fields so
  // no nested Map/Set restoration is needed. The hard-reject version
  // gate at the top of this function guarantees body.projectiles is
  // present (pre-v13 saves never reach this point).
  //
  // v4M9.1b — collection renamed `missiles` → `projectiles` on both
  // body and runtime state. SAVE_BLOB_VERSION bump to 20 rejects pre-
  // rename blobs (which carry `body.missiles`) up front.
  const projectiles = new Map<number, Projectile>();
  for (const [id, m] of body.projectiles) {
    // MFix29.1 — slotPerp added in SAVE_BLOB_VERSION 27. Pre-v27
    // blobs are rejected at the version gate, so any projectile read
    // here either carries slotPerp from disk or — defensively, for
    // in-memory shapes built without it — defaults to 0 (no slot
    // offset; pulse render falls back to firer centerline).
    projectiles.set(id, { ...m, slotPerp: m.slotPerp ?? 0 });
  }

  // v4M9.5 — restore in-flight beam-sustain channels from the save body.
  // BeamFire carries only flat number/string fields so a shallow clone
  // suffices. The hard-reject SAVE_BLOB_VERSION gate guarantees
  // body.beams is present (pre-v24 blobs reject up front).
  const beams = new Map<number, BeamFire>();
  for (const [id, b] of body.beams) {
    // MFix19 — slotIndex was added in SAVE_BLOB_VERSION 26. Pre-v26
    // blobs are rejected at the version gate, so any beam read here
    // either carries slotIndex from disk or — defensively, for in-
    // memory shapes built without it — defaults to 0 (single-channel
    // render, the pre-MFix19 behaviour).
    beams.set(id, { ...b, slotIndex: b.slotIndex ?? 0 });
  }

  const structures = new Map<number, Structure>();
  for (const [id, s] of body.structures) structures.set(id, { ...s });

  const environment = new Map<number, EnvironmentEntity>();
  for (const [id, e] of body.environment) {
    // v4M13.4a lenient back-compat — pre-v32 blobs carried the
    // `'gas-giant'` EnvironmentKind value before the
    // `'exotic-gas-cloud'` rename. Translate on load so the runtime
    // type union never sees the retired string. Cast through unknown
    // because the typed field doesn't include `'gas-giant'` anymore.
    const rawKind = (e as unknown as { kind: string }).kind;
    const kind = rawKind === 'gas-giant' ? 'exotic-gas-cloud' : rawKind;
    environment.set(id, { ...e, kind: kind as EnvironmentEntity['kind'] });
  }

  // v4M11.1 — pre-v30 blob back-compat per `IN_SYSTEM_FOG.md §3`.
  // SerializedEmpire entries from pre-v30 blobs have no
  // `discoveredPlanetIds` / `discoveredEnvironmentIds` fields, so the
  // `deserializeEmpire` defensive seed left them empty. Reconstruct
  // by walking each scouted system: every planet + environment entity
  // in a scouted system was visible in the pre-fog renderer, so the
  // post-load discovered state matches the pre-load visible state.
  // Detection: probe the first serialized empire — if it lacks the
  // new field, treat the whole blob as pre-v30 and backfill every
  // empire (a v30 blob would have the field on every empire).
  const firstEmpireEntry = body.empires[0];
  const isPreV30 =
    firstEmpireEntry !== undefined &&
    (firstEmpireEntry[1] as { discoveredPlanetIds?: number[] })
      .discoveredPlanetIds === undefined;
  if (isPreV30) {
    // Build per-system entity index once so the backfill is O(empires
    // × scoutedSystemIds × entities-per-system) rather than O(empires
    // × scoutedSystemIds × total-entities).
    const planetsBySystem = new Map<number, number[]>();
    for (const planet of planets.values()) {
      const bucket = planetsBySystem.get(planet.systemId) ?? [];
      bucket.push(planet.id);
      planetsBySystem.set(planet.systemId, bucket);
    }
    const environmentBySystem = new Map<number, number[]>();
    for (const env of environment.values()) {
      const bucket = environmentBySystem.get(env.systemId) ?? [];
      bucket.push(env.id);
      environmentBySystem.set(env.systemId, bucket);
    }
    for (const empire of empires.values()) {
      for (const sysId of empire.scoutedSystemIds) {
        const planetIds = planetsBySystem.get(sysId);
        if (planetIds) {
          for (const pid of planetIds) empire.discoveredPlanetIds.add(pid);
        }
        const envIds = environmentBySystem.get(sysId);
        if (envIds) {
          for (const eid of envIds) empire.discoveredEnvironmentIds.add(eid);
        }
      }
    }
  }

  return {
    version: body.version,
    tick: body.tick,
    rng: { s: body.rng.s >>> 0 },
    nextEntityId: body.nextEntityId,
    empires,
    planets,
    systems,
    warpGateLinks,
    ships,
    projectiles,
    structures,
    environment,
    shipTemplates: data.shipTemplates,
    auras: data.auras,
    weapons: data.weaponDefs,
    techs: data.techDefs,
    habitability: data.habitability,
    aiBehaviours: data.aiBehaviours,
    races: data.races,
    structuresData: data.structures,
    balanceData: data.balance,
    teamVision: deserializeTeamVision(body.teamVision),
    // v4M10.1 — lenient back-compat: pre-v30 blobs lack the field;
    // empty seed gets re-populated by the first post-load tick's
    // capture pass per `docs/FOW.md §3.3`.
    fogOfWarSnapshots: deserializeFogOfWarSnapshots(body.fogOfWarSnapshots),
    // v3M19 — `combatEvents` is transient render-layer state, deliberately
    // excluded from the save body. Always re-attach as an empty array on
    // load; the next `advanceCombat` pass will populate it as combat
    // resolves.
    combatEvents: [],
    // v4M1.3 — `commandRejections` is transient UI-layer state populated by
    // `applyCommands` and consumed by the alert-feed on the same tick.
    // Excluded from the save body; re-attach as an empty array on load.
    commandRejections: [],
    // MFix11.5 — `weaponFireEvents` is transient render-layer state
    // populated by `advance-combat` and drained by `advanceVfx` on the
    // same tick. Excluded from the save body; re-attach as an empty
    // array on load.
    weaponFireEvents: [],
    // MFix42.5b — `bombardmentEvents` is transient render-layer state
    // populated by `advance-bombardment` and drained by `advanceVfx`
    // on the same tick. Excluded from save body; re-attach as empty.
    bombardmentEvents: [],
    // MFix12.4 — `pendingShots` is transient deferred-shot queue for
    // Railgun's inter-slot stagger. Excluded from save body (mid-
    // combat shots in flight are lost); re-attach as empty on load.
    pendingShots: [],
    // v4M9.1a - weaponMechanics is a static catalogue loaded from
    // src/data/weapon-mechanics.json (built from
    // docs/weapon_mechanics.xlsx). Same lifecycle as structuresData
    // and other xlsx-derived catalogues: re-attach from data on
    // load rather than from the save body.
    weaponMechanics: data.weaponMechanics,
    // v4M9.1a — beams is the in-flight beam-sustain entity map.
    // v4M9.5 — made persistent: restored from body.beams above.
    beams,
    playerEmpireId: body.playerEmpireId,
    // v3M12 - older blobs (pre-v3M12) won't have gameOutcome; default
    // them to 'in-progress' so legacy saves keep behaving like an
    // active game when loaded.
    gameOutcome: body.gameOutcome ?? 'in-progress',
  };
}